
import { ReactNode } from "react";

import { 
    AppEnum, 
    ContactEnums, 
    FaHashtag, 
    FaPhone, 
    FaSearch, 
    FaUser, 
    FaUserTag,
    Header,
    OptionHelper,
    RecordMap
 } from "@/Shared";

import { ContactViewModel } from "../Logic/ViewModels";
import { UserModel } from "@/Domains";


export type SelectOption = {
    value : any,
    label : any
}

/**
 * Class: ContactHeader
 *
 * Description:
 * Centralized header definitions for Contact tables/grids.
 * Provides default headers, icons, filtering, and sorting behavior.
 * Can build a complete record of headers for a ContactViewModel.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class ContactHeader {

     /**
     * Private constructor to prevent instantiation
     * Only static build method should be used
     */
    private constructor() { }

    /**
     * =====================================================
     * Individual Header Definitions
     * =====================================================
     */
    get id() {
        return {
            key: 'id',
            icon: <FaHashtag />,
            label: 'ID',
            type: 'number',
            isSortable: true
        }
    }

    get civility() {
        return {
            key: 'civility',
            icon: <FaUser />,
            label: 'Civilité',
            type: 'string',
            isSortable: true
        }
    }
    get name() {
        return {
            key: 'fullName',
            icon: <FaSearch />,
            label: 'Nom ou Prénom',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Par nom",
                case: 'insensitive'
            }
        }
    }
    getContactType(ContactTypeEnum : AppEnum[]) {
        return {
            key: 'contactType',
            icon: <FaUserTag />,
            label: 'Type',
            type: 'string',
            isSortable: true,
            filter: {
                label: "Type",
                options: ContactTypeEnum,
                case: 'sensitive'
            }
        }
    }

    get phone() {
        return {
            key: 'phone',
            icon: <FaPhone />,
            label: 'Téléphone(s)',
            type: 'string',
            isSortable: true
        }
    }
    get email() {
        return {
            key: 'email',
            icon: <FaSearch />,
            label: 'Email',
            type: 'string',
            isSortable: true,
            isHidden: false,
            filter: {
                label: "Par Email",
                case: 'insensitive'
            }
        }
    }

    agent(userMap: RecordMap<UserModel>) {
        return {
            key: 'agent',
            icon: <FaUser />,
            label: 'Référent',
            type: 'string',
            isSortable: true,
            isHidden: true,
            filter: {
                label: "Suivi Par",
                case: 'sensitive',
                options:  OptionHelper.userOptions(userMap.toArray())
            }
        }
    }

     /**
     * =====================================================
     * Static Builder
     * =====================================================
     *
     * Builds a complete header record for a ContactViewModel
     *
     * @param userMap - List of users for filtering agent column
     * @param viewEnums - ContactEnums for filtering contactType
     * @returns Record of headers keyed by ContactViewModel properties
     */
    static build(userMap:  RecordMap<UserModel>, viewEnums : ContactEnums): Record<keyof ContactViewModel, Header<ContactViewModel>> {
        const header = new ContactHeader();
        return {
            id: header.id,
            civility: header.civility,
            fullName: header.name,
            contactType: header.getContactType(viewEnums.ContactTypeEnum),
            phone: header.phone,
            email: header.email,
            agent: header.agent(userMap)
        } as Record<keyof ContactViewModel, Header<ContactViewModel>>
    }
}